import { Coordinate, LightsContainer, ScreenDimensions, TileSize, Visibility } from "game-common/models"
import { getSightPolygon, pruneSegments, VectorLine } from "game-common/ray_casting"
import { Application, Container, filters, Graphics, Rectangle, RenderTexture, Sprite, Texture } from "pixi.js"
import { ClientGameLogic } from "../../../client_game_logic"
import { logger } from "../../../logger"
import { TranslationComputer } from "./translation_computer"
import { SCALER, ScalingParams } from "../../../client_util"

interface VisionOptions {
    polyMap: Array<VectorLine>
    location: Coordinate
    debug?: Graphics
    visibility?: Visibility
    lights?: LightsContainer
}

export class ShadowRendering {
    private lastLocation: Coordinate = { x: -1, y: -1 }
    private visibilityTexture: Texture
    private visibilitySprite: Sprite
    private visibilityBounds: Rectangle
    private visibilitySurface: Graphics
    private visibilityLastUpdateTs?: number
    private app: Application
    private surface: Container
    private cleared: boolean = true
    private lightLastChangedTs?: number
    private latestDarkness: number
    private logic: ClientGameLogic
    private roomId: string
    private translationComputer = new TranslationComputer()

    // NEW: Texture caching and dirty flagging properties
    private needsTextureUpdate: boolean = false
    private lastVisibilityHash: string = ""
    private cachedVisibilityHash: string = ""
    private textureCache: Map<string, Texture> = new Map()
    private maxCacheSize: number = 10 // Limit cache size to prevent memory leaks
    public debugHashCalculation: boolean = false // Debug flag for hash calculation logging

    constructor(app: Application, surface: Container, debug: boolean, logic: ClientGameLogic) {
        this.app = app
        this.surface = surface
        this.logic = logic

        const visibilitySurface = new Graphics()
        if (!debug) {
            visibilitySurface.filters = [new filters.BlurFilter(50)]
        }
        visibilitySurface.scale.set(ScalingParams.surfaceZoom)
        this.visibilitySurface = visibilitySurface
        this.translationComputer.noDelay = true
    }

    renderVisibility = (visionOptions: VisionOptions): boolean => {
        const shouldRender = this.prepare(visionOptions)
        if (!shouldRender) {
            return false
        }

        // NEW: Only render if we need to update the texture
        if (this.needsTextureUpdate) {
            this.render()
            this.needsTextureUpdate = false
        }

        return true
    }

    private prepare = ({ polyMap, location, visibility, debug, lights }: VisionOptions): boolean => {
        if (!this.logic.tilemapMetaProvider) {
            return false
        }
        const darkness = visibility?.darkness
        const hasDarkness = darkness < 1.0
        const hasPolymap = polyMap?.length > 0

        if (this.roomId !== this.logic.tilemapMetaProvider?.roomId) {
            this.lastLocation = { x: -1, y: -1 }
            this.translationComputer.update(this.logic.tilemapMetaProvider)
            this.roomId = this.logic.tilemapMetaProvider.roomId
            // NEW: Clear cache on room change
            this.clearTextureCache()
        }

        if (!hasPolymap && !hasDarkness && this.cleared === false) {
            this.translationComputer.reset()
            this.visibilitySurface.clear()
            this.cleared = true
            this.surface.mask = undefined
            if (this.visibilitySprite) {
                this.app.stage.removeChild(this.visibilitySprite)
            }
            this.lastLocation = { x: -1, y: -1 }
            // NEW: Clear cache when visibility is disabled
            this.clearTextureCache()
            return false
        }

        const lightsChanged = this.lightLastChangedTs !== lights?.lastUpdatedTs
        const playerLocationChanged = !(location.x === this.lastLocation.x && location.y === this.lastLocation.y)
        const darknessChanged = this.latestDarkness !== darkness
        // The mask is baked in stage coordinates. Include the newly exposed areas on all edges
        // and rebake after resizing even when the player and lights have not moved.
        const left = 0 - this.app.stage.x
        const top = 0 - this.app.stage.y / SCALER
        const viewportChanged = !this.visibilityBounds || this.visibilityBounds.x !== left || this.visibilityBounds.y !== top
        if (viewportChanged) {
            this.visibilityBounds = new Rectangle(
                left, top, 27 * TileSize + this.app.stage.x * 2, 16 * TileSize + this.app.stage.y * 2 / SCALER,
            )
        }
        if (this.visibilitySprite) this.visibilitySprite.position.set(left, top)

        // NEW: Calculate visibility hash to determine if update is needed
        const hashState = {
            polyMap,
            location,
            visibility,
            lights,
            playerLocationChanged,
            lightsChanged,
            darknessChanged,
        }

        if (this.debugHashCalculation) {
            this.debugHashCalculationLog(hashState)
        }

        const currentVisibilityHash = this.computeVisibilityHash(hashState)

        if (this.debugHashCalculation) {
            logger.debug(
                { tags: ["shadow", "cache", "debug"] },
                `[Shadow Cache] Current hash: ${currentVisibilityHash}, Cached hash: ${this.cachedVisibilityHash}`,
            )
        }

        // NEW: Check if we can reuse a cached texture first
        const cachedTexture = this.textureCache.get(currentVisibilityHash)
        if (cachedTexture) {
            // Cache hit - reuse existing texture
            if (this.visibilitySprite) {
                this.visibilitySprite.texture = cachedTexture
            }
            this.cachedVisibilityHash = currentVisibilityHash
            this.needsTextureUpdate = false // Mark as cache hit
            if (this.debugHashCalculation) {
                logger.debug(
                    { tags: ["shadow", "cache", "hit"] },
                    `[Shadow Cache] Cache HIT for hash: ${currentVisibilityHash}`,
                )
            }
            return true
        }

        // NEW: Check if we already have the same hash (no changes needed)
        if (currentVisibilityHash === this.cachedVisibilityHash) {
            this.needsTextureUpdate = false // Mark as cache hit
            if (this.debugHashCalculation) {
                logger.debug(
                    { tags: ["shadow", "cache", "hit"] },
                    `[Shadow Cache] Same hash, no update needed: ${currentVisibilityHash}`,
                )
            }
            return true
        }

        // Check if we should skip expensive operations based on old logic
        if (!hasPolymap) {
            // there isn't a poly map. In this case, then we don't care about player location changes,
            // only pure darkness changes
            if (
                !hasDarkness ||
                (this.translationComputer.ready() &&
                    !viewportChanged && !darknessChanged && !playerLocationChanged && !lightsChanged)
            ) {
                return false
            }
        } else {
            // there's a poly map. in this case we care about player changes
            if (!viewportChanged && !playerLocationChanged && !lightsChanged) {
                if (this.visibilityLastUpdateTs === visibility?.lastUpdateTs) {
                    return false
                }
            }
        }

        if (this.visibilityLastUpdateTs !== visibility?.lastUpdateTs) {
            this.visibilityLastUpdateTs = visibility?.lastUpdateTs
        }

        this.lightLastChangedTs = lights.lastUpdatedTs
        this.latestDarkness = darkness
        this.cleared = false

        const g = this.visibilitySurface

        g.clear()

        const playerLocation = { x: location.x, y: location.y }

        this.translationComputer.compute(playerLocation)
        let { modifierX, modifierY, xStatus, yStatus } = this.translationComputer

        if (modifierX === undefined) {
            if (xStatus === "left") {
                modifierX = -180
            }
            if (xStatus === "right") {
                modifierX = -1330
            }
        }
        if (modifierY === undefined) {
            if (yStatus === "top") {
                modifierY = -80
            }
            if (yStatus === "bottom") {
                modifierY = -1420
            }
        }
        if (modifierX !== undefined) {
            // g.transform.position.x = ((-playerLocation.x + modifierX + ScreenDimensions.w / 2) * 2) - 32 * 11
            g.transform.position.x =
                (modifierX + ScreenDimensions.w / 2) * ScalingParams.surfaceZoom - 48 * ScalingParams.viewportX
        }

        if (modifierY !== undefined) {
            // g.transform.position.y = ((-playerLocation.y + modifierY + ScreenDimensions.h / 2) * 2) - 32 * 7
            g.transform.position.y =
                (modifierY + ScreenDimensions.h / 2) * ScalingParams.surfaceZoom - 48 * ScalingParams.viewportY
        }

        if (hasPolymap) {
            const vs = visibility?.range || 600

            // Top
            polyMap[0] = {
                a: { x: playerLocation.x - vs, y: playerLocation.y - vs },
                b: { x: playerLocation.x + vs, y: playerLocation.y - vs },
            }
            // Bottom
            polyMap[1] = {
                a: { x: playerLocation.x - vs, y: playerLocation.y + vs },
                b: { x: playerLocation.x + vs, y: playerLocation.y + vs },
            }
            // Left
            polyMap[2] = {
                a: { x: playerLocation.x - vs, y: playerLocation.y - vs },
                b: { x: playerLocation.x - vs, y: playerLocation.y + vs },
            }
            // Right
            polyMap[3] = {
                a: { x: playerLocation.x + vs, y: playerLocation.y - vs },
                b: { x: playerLocation.x + vs, y: playerLocation.y + vs },
            }

            const prunedSegments = pruneSegments(polyMap, playerLocation.x, playerLocation.y, vs, 3)

            let polygons = [getSightPolygon(prunedSegments, playerLocation.x, playerLocation.y)]
            if (debug) {
                // debug.transform.position.x = -playerLocation.x + 50 + ScreenDimensions.w / 2
                // debug.transform.position.y = -playerLocation.y + 50 + ScreenDimensions.h / 2

                debug.transform.position.x =
                    (-playerLocation.x + 50 + ScreenDimensions.w / 2) * 2 - 32 * ScalingParams.viewportX
                debug.transform.position.y =
                    (-playerLocation.y + 50 + ScreenDimensions.h / 2) * 2 - 32 * ScalingParams.viewportY

                debug.clear()
                const lineMap = prunedSegments.reduce((acc, x) => {
                    acc.push(x.a)
                    acc.push(x.b)
                    return acc
                }, [])

                debug.beginFill(0xff0000, 0.95)
                for (let point of lineMap) {
                    debug.drawCircle(point.x, point.y, 3)
                }
                debug.endFill()

                debug.beginFill(0x00ff00, debug ? 0.2 : 1)
                for (let polygon of polygons) {
                    for (let point of polygon) {
                        debug.drawCircle(point.x, point.y, 3)
                    }
                }
                debug.endFill()

                debug.beginFill(0xff0000, 0.2)
                for (let polygon of polygons) {
                    debug.drawPolygon(polygon)
                }
                debug.endFill()
            }

            g.beginFill(0xff0000, darkness)
            for (let polygon of polygons) {
                g.drawPolygon(polygon)
            }
            g.endFill()
        }

        if (darkness !== undefined) {
            g.beginFill(0xfffffff, hasPolymap ? 0.01 : darkness)
            // for now -- hardcode to large map size
            g.drawRect(
                0,
                0,
                this.logic.tilemapMetaProvider.width() * TileSize,
                this.logic.tilemapMetaProvider.height() * TileSize,
            )
            g.endFill()
        }

        if (darkness !== undefined && lights) {
            lights.iterate(light => {
                const {
                    location: { x, y },
                    intensity,
                    radius,
                } = light
                g.beginFill(0xffffff, intensity)
                g.drawCircle(x, y, radius)
                g.endFill()
            })
        }

        this.lastLocation.x = location.x
        this.lastLocation.y = location.y

        // NEW: Mark that texture needs update and update cached hash
        this.needsTextureUpdate = true
        this.cachedVisibilityHash = currentVisibilityHash

        if (this.debugHashCalculation) {
            logger.debug(
                { tags: ["shadow", "cache", "miss"] },
                `[Shadow Cache] Cache MISS for hash: ${currentVisibilityHash}`,
            )
        }

        return true
    }

    private render = () => {
        // NEW: Check if we can reuse a cached texture
        const currentHash = this.cachedVisibilityHash
        let textureToUse = this.textureCache.get(currentHash)

        if (!textureToUse) {
            // Create new texture. Do NOT destroy this.visibilityTexture here: it is always a member
            // of textureCache (see line where textureToUse is assigned below), so destroying it would
            // leave a freed texture (baseTexture === null) sitting in the cache. A later cache hit on
            // that hash would then assign the dead texture to the sprite and crash in Sprite.set
            // ("Cannot read properties of null (reading 'valid')"). Texture lifetime is owned solely
            // by the cache (manageCacheSize / clearTextureCache).
            textureToUse = this.app.renderer.generateTexture(this.visibilitySurface, {
                region: this.visibilityBounds,
            })

            // NEW: Cache the new texture
            this.textureCache.set(currentHash, textureToUse)
            this.manageCacheSize()

            if (this.debugHashCalculation) {
                logger.debug(
                    { tags: ["shadow", "cache", "create"] },
                    `[Shadow Cache] Created and cached texture for hash: ${currentHash}`,
                )
            }
        } else {
            if (this.debugHashCalculation) {
                logger.debug(
                    { tags: ["shadow", "cache", "reuse"] },
                    `[Shadow Cache] Reusing cached texture for hash: ${currentHash}`,
                )
            }
        }

        this.visibilityTexture = textureToUse

        if (!this.visibilitySprite) {
            this.visibilitySprite = new Sprite(this.visibilityTexture)
        } else {
            this.visibilitySprite.texture = this.visibilityTexture
        }
        this.surface.mask = this.visibilitySprite

        this.visibilitySprite.x = this.visibilityBounds.x
        this.visibilitySprite.y = this.visibilityBounds.y

        this.app.stage.addChild(this.visibilitySprite)
    }

    // --- PiP (follow-cam) light mask ------------------------------------------------------------
    // The main mask (visibilitySprite above) is baked in the PLAYER's screen space, so reusing it for
    // the ember follow-cam — which frames a DIFFERENT entity — makes every light drift across the PiP
    // as the player walks. This builds a SECOND alpha mask, baked in the PiP's OWN screen space: a dim
    // base fill plus brighter "holes" at each light's world position, transformed by the same
    // (cx, cy, zoom) the PiP world render uses (see renderWorldToTexture). Because it shares that
    // framing, each light stays fixed over the world regardless of where the player is.
    //
    // Alignment: the returned sprite is used verbatim as `surface.mask` for the isolated PiP render.
    // It is never parented, so its worldTransform stays identity, which makes the SpriteMaskFilter map
    // mask-texture pixel (X,Y) → render-texture pixel (X,Y) 1:1 (same contract the main mask relies
    // on). We therefore bake the framing into the texture, not the sprite transform.
    private pipDarkness?: Graphics
    private pipMaskTexture?: RenderTexture
    private pipMaskSprite?: Sprite

    renderPipLightMask = (opts: {
        cx: number
        cy: number
        zoom: number
        texW: number
        texH: number
        resolution: number
        visibility?: Visibility
        lights?: LightsContainer
    }): Sprite | undefined => {
        const { cx, cy, zoom, texW, texH, resolution, visibility, lights } = opts
        const darkness = visibility?.darkness
        // No darkness in this room → no mask (the PiP renders at full brightness, same as the world).
        if (darkness === undefined || darkness >= 1.0) {
            return undefined
        }

        if (!this.pipDarkness) {
            this.pipDarkness = new Graphics()
            // Soften the light edges, mirroring the main mask's blur (scaled down for the PiP zoom).
            this.pipDarkness.filters = [new filters.BlurFilter(10)]
        }
        if (
            !this.pipMaskTexture ||
            this.pipMaskTexture.width !== texW ||
            this.pipMaskTexture.height !== texH ||
            this.pipMaskTexture.baseTexture.resolution !== resolution
        ) {
            this.pipMaskTexture?.destroy(true)
            this.pipMaskTexture = RenderTexture.create({ width: texW, height: texH, resolution })
            this.pipMaskSprite = new Sprite(this.pipMaskTexture)
        }

        const g = this.pipDarkness
        g.clear()

        // Base dim fill over just the visible PiP region (a small margin covers the blur bleed at the
        // edges). Bounding it to the view keeps the blur-filter texture small instead of map-sized.
        const halfW = texW / zoom / 2
        const halfH = texH / zoom / 2
        const margin = 48
        g.beginFill(0xffffff, darkness)
        g.drawRect(cx - halfW - margin, cy - halfH - margin, (halfW + margin) * 2, (halfH + margin) * 2)
        g.endFill()

        // Brighter "holes" at each in-view light's world position (matches prepare()'s light pass).
        lights?.iterate(light => {
            const {
                location: { x, y },
                intensity,
                radius,
            } = light
            if (Math.abs(x - cx) > halfW + radius || Math.abs(y - cy) > halfH + radius) {
                return
            }
            g.beginFill(0xffffff, intensity)
            g.drawCircle(x, y, radius)
            g.endFill()
        })

        // Bake the world→PiP-texture framing into the graphics transform (identical to the surface
        // transform in renderWorldToTexture), so the mask lines up pixel-for-pixel with the world.
        g.position.set(texW / 2 - cx * zoom, texH / 2 - cy * zoom)
        g.scale.set(zoom)

        this.app.renderer.render(g, { renderTexture: this.pipMaskTexture, clear: true })

        return this.pipMaskSprite
    }

    // NEW: Compute a hash of the current visibility state
    private computeVisibilityHash(state: {
        polyMap: Array<VectorLine>
        location: Coordinate
        visibility: Visibility
        lights: LightsContainer
        playerLocationChanged: boolean
        lightsChanged: boolean
        darknessChanged: boolean
    }): string {
        const { polyMap, location, visibility, lights } = state

        // Create a hash based on key factors that affect visibility.
        //
        // The location goes in at FULL precision. It used to be quantized to a 32px tile, which is
        // wrong for a mask baked in SCREEN space: prepare() positions the graphics with the camera
        // modifier taken from this exact location, so two positions inside one tile bake to
        // different places. Quantizing them to the same hash froze the mask while the world went on
        // scrolling, and every light -- the player's own and every campfire's -- slid against the
        // ground by however far the camera had moved, then snapped back on the next real bake.
        //
        // It survived locally because the OTHER half of the hash is lights.lastUpdatedTs, and the
        // lights are re-registered by entity_rendering_manager.update(), whose only callers are the
        // server-message handlers. On localhost an update lands on essentially every frame, so the
        // hash changed anyway; at 50ms it lands on ~3 frames in 4, and the rest drew a stale mask.
        // Measured on production: up to 9px of slide per snap in ordinary walking (~20 CSS px at a
        // typical canvas scale), and 50px across a burst of late frames.
        const locationKey = `${location.x}-${location.y}`
        const darknessKey = visibility?.darkness?.toString() || "1.0"
        const lightsKey = lights?.lastUpdatedTs?.toString() || "0"
        const polyMapKey = polyMap?.length?.toString() || "0"
        const visibilityKey = visibility?.lastUpdateTs?.toString() || "0"

        return `${locationKey}-${darknessKey}-${lightsKey}-${polyMapKey}-${visibilityKey}-${this.app.stage.x}-${this.app.stage.y}`
    }

    // NEW: Clear texture cache to prevent memory leaks
    private clearTextureCache = () => {
        this.textureCache.forEach(texture => texture.destroy(true))
        this.textureCache.clear()
        this.cachedVisibilityHash = ""
        this.needsTextureUpdate = true
    }

    // NEW: Manage cache size to prevent memory leaks
    private manageCacheSize = () => {
        if (this.textureCache.size > this.maxCacheSize) {
            // Remove oldest entries (simple FIFO)
            const entries = Array.from(this.textureCache.entries())
            const toRemove = entries.slice(0, entries.length - this.maxCacheSize)
            toRemove.forEach(([key, texture]) => {
                // Never evict/destroy the texture currently bound to the mask sprite; freeing it
                // would leave the sprite pointing at a dead texture and crash on the next frame.
                if (texture === this.visibilityTexture) {
                    return
                }
                texture.destroy(true)
                this.textureCache.delete(key)
            })
        }
    }

    // NEW: Public method to force texture update (useful for debugging)
    forceTextureUpdate = () => {
        this.needsTextureUpdate = true
        this.clearTextureCache()
    }

    // NEW: Get cache statistics for monitoring
    getCacheStats = () => {
        return {
            cacheSize: this.textureCache.size,
            maxCacheSize: this.maxCacheSize,
            lastVisibilityHash: this.cachedVisibilityHash,
            needsUpdate: this.needsTextureUpdate,
        }
    }

    // NEW: Debug method to log current state
    debugLog = () => {
        const stats = this.getCacheStats()
        logger.debug(
            { tags: ["shadow", "debug", "cache"] },
            `[Shadow Debug] Cache: ${stats.cacheSize}/${stats.maxCacheSize}, Needs Update: ${stats.needsUpdate}, Hash: ${stats.lastVisibilityHash}`,
        )
    }

    // NEW: Debug method to log hash calculation details
    debugHashCalculationLog = (state: any) => {
        const { polyMap, location, visibility, lights, playerLocationChanged, lightsChanged, darknessChanged } = state

        const locationKey = `${location.x}-${location.y}`
        const darknessKey = visibility?.darkness?.toString() || "1.0"
        const lightsKey = lights?.lastUpdatedTs?.toString() || "0"
        const polyMapKey = polyMap?.length?.toString() || "0"
        const changeFlags = `${playerLocationChanged ? "1" : "0"}${lightsChanged ? "1" : "0"}${
            darknessChanged ? "1" : "0"
        }`

        logger.debug(
            { tags: ["shadow", "debug", "hash"] },
            `[Shadow Hash Debug] Location: ${locationKey}, Darkness: ${darknessKey}, Lights: ${lightsKey}, PolyMap: ${polyMapKey}, Changes: ${changeFlags}`,
        )
    }
}
